/**
 * Row for a report.
 */
import * as React from 'react';
import { Calendar, User } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { Rating } from '@/components/forms/Rating';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { getRowPermission } from '@/hooks/usePageActions';
import type { SessionReport } from '@/types/inperson/report.types';
import { DataTableRowCheckbox } from '@/components/common/data-table/DataTableRowCheckbox';
import {
  deriveReportDisplay,
  formatReportSessionDateLabel,
  getReportActionState,
  resolveReportPermissions,
  type DerivedReportDisplay,
  type ReportPageActions,
} from './report-table-utils';

/** Props for {@link ReportRow}. */
interface ReportRowProps {
  report: SessionReport;
  onView?: (reportId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onValidate?: () => void;
  pageActions?: ReportPageActions;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

/** Checkbox cell for bulk-selecting one report row. */
function SelectCell({
  isSelected,
  onToggleSelect,
}: Readonly<{
  isSelected: boolean;
  onToggleSelect?: () => void;
}>) {
  return (
    <td className="w-10 text-center">
      <div className="flex items-center justify-center">
        <DataTableRowCheckbox checked={isSelected} onCheckedChange={() => onToggleSelect?.()} />
      </div>
    </td>
  );
}

/** Session title and establishment under the calendar icon. */
function SessionCell({
  sessionDisplayText,
  establishmentName,
}: Readonly<{
  sessionDisplayText: string;
  establishmentName: string | null;
}>) {
  return (
    <td>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="font-medium text-foreground">{sessionDisplayText}</span>
        </div>
        {establishmentName && (
          <div className="ml-10">
            <span className="text-sm text-muted-foreground">{establishmentName}</span>
          </div>
        )}
      </div>
    </td>
  );
}

/** Author name cell with the user icon. */
function AuthorCell({ authorName }: Readonly<{ authorName: string }>) {
  return (
    <td>
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
          <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
        </div>
        <span className="text-foreground">{authorName}</span>
      </div>
    </td>
  );
}

/** Star rating or “not rated” placeholder. */
function RatingCell({ displayRating }: Readonly<{ displayRating: number | null }>) {
  if (displayRating === null) {
    return (
      <td>
        <span className="text-sm text-muted-foreground">Non noté</span>
      </td>
    );
  }
  return (
    <td>
      <Rating value={displayRating} readOnly size="sm" showLabel={false} />
    </td>
  );
}

/** Status badge derived from the report display config. */
function StatusCell({ display }: Readonly<{ display: DerivedReportDisplay }>) {
  return (
    <td>
      <LabelBadge
        label={display.statusConfig.label}
        icon={display.StatusIcon}
        variant={display.statusConfig.badgeVariant}
        color={display.statusConfig.badgeColor}
        size="md"
      />
    </td>
  );
}

/** Filled-at date cell for one report row. */
function EntryDateCell({ entryDateText }: Readonly<{ entryDateText: string }>) {
  return (
    <td>
      <span className="text-sm text-foreground">{entryDateText}</span>
    </td>
  );
}

/** View / edit / validate / delete actions for one report row. */
function ActionsCell({
  reportId,
  canEdit,
  canValidate,
  canDelete,
  editDisabled,
  editTooltip,
  validateDisabled,
  validateTooltip,
  onView,
  onEdit,
  onDelete,
  onValidate,
}: Readonly<{
  reportId: string;
  canEdit: boolean;
  canValidate: boolean;
  canDelete: boolean;
  editDisabled: boolean;
  editTooltip: string;
  validateDisabled: boolean;
  validateTooltip: string;
  onView?: (reportId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onValidate?: () => void;
}>) {
  return (
    <td className="text-center">
      <div className="flex items-center justify-center gap-1">
        <Button preset="row-view" onClick={() => onView?.(reportId)} />
        {canEdit && (
          <Button
            preset="row-edit"
            disabled={editDisabled}
            tooltipText={editTooltip}
            onClick={() => onEdit?.()}
          />
        )}
        {canValidate && (
          <Button
            preset="row-validate"
            disabled={validateDisabled}
            tooltipText={validateTooltip}
            onClick={() => onValidate?.()}
          />
        )}
        {canDelete && <Button preset="row-delete" onClick={() => onDelete?.()} />}
      </div>
    </td>
  );
}

/** Table row for an in-person report. */
export const ReportRow: React.FC<ReportRowProps> = ({
  report,
  onView,
  onEdit,
  onDelete,
  onValidate,
  pageActions,
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const { canEdit: canEditResolved, canValidate } = resolveReportPermissions(
    report.session?.type ?? '',
    pageActions,
  );
  const canEdit = getRowPermission(report, 'action-edit-row', canEditResolved);
  const canDelete = getRowPermission(
    report,
    'action-delete-row',
    pageActions?.canDeleteRow ?? true,
  );

  const display = deriveReportDisplay(report);
  const sessionDisplayText = formatReportSessionDateLabel(report);
  const actionState = getReportActionState(report);

  return (
    <TableRow>
      {isSelectable && <SelectCell isSelected={isSelected} onToggleSelect={onToggleSelect} />}
      <SessionCell
        sessionDisplayText={sessionDisplayText}
        establishmentName={display.establishmentName}
      />
      <AuthorCell authorName={display.authorName} />
      <RatingCell displayRating={display.displayRating} />
      <StatusCell display={display} />
      <EntryDateCell entryDateText={display.entryDateText} />
      <ActionsCell
        reportId={report.id}
        canEdit={canEdit}
        canValidate={canValidate}
        canDelete={canDelete}
        editDisabled={actionState.editDisabled}
        editTooltip={actionState.editTooltip}
        validateDisabled={actionState.validateDisabled}
        validateTooltip={actionState.validateTooltip}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        onValidate={onValidate}
      />
    </TableRow>
  );
};
